import { ErrorCode, isServerError } from "api";
import { ChevronRightIcon } from "assets";
import classNames from "classnames";
import { COOKIE_NAME, links } from "data";
import { LoginErrorCode, LoginPayload, useAuth } from "domain/authentication";
import { AuthActionNeeded, User } from "domain/users";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "router";
import { track } from "shared/analytics";
import { PlainButton } from "shared/components";
import { useLoading } from "shared/hooks";
import { emailSchema, requiredString } from "shared/yup-validations";
import { ButtonProps } from "types";
import * as yup from "yup";
import { getParams } from "../common";
import styles from "./login.page.module.scss";
import { jwtDecode } from "jwt-decode";
import { useCookies } from "react-cookie";

const ERROR_MAPPER = {
  [LoginErrorCode.InvalidCredentials]:
    "That email address or password is not recognised. Please try again.",
  [LoginErrorCode.UserDisabled]:
    "Sorry, you have reached the maximum number of login attempts. Your account has been locked. Please contact CitySprint on 01793 836075 or send an email to salessupport@citysprint.co.uk",
  [ErrorCode.NetworkError]:
    "The application is facing technical issues at the moment. Please contact CitySprint on 01793 836075 or send an email to salessupport@citysprint.co.uk",
  [ErrorCode.ServerError]:
    "The application is facing technical issues at the moment. Our team is working on remedying the problem. Please try again later.",
};

const SAMEDAY_BASE_URL = process.env.REACT_APP_SAMEDAY_BASE_URL;
const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;
const FORGOT_PASSWORD_URL = `${SAMEDAY_BASE_URL}/SameDayRedesign/User/ForgotPassword.aspx?ReturnURL=${REACT_APP_BASE_URL}`;

export const LoginPage = () => {
    const { user, defaultAccount, logOut } = useAuth();
    const [cookies] = useCookies([COOKIE_NAME]);
    const navigate = useNavigate();
    const didSubmitLogin = useRef(false);  

  useEffect(() => {
    const token = cookies.userToken;
    if (!token || !user || !defaultAccount) return;

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp && (decodedToken.exp < currentTime)) {
        logOut();
        return;
      }
    } catch {
      // Not a JWT — continue to the actionNeeded handling.
    }

    const params = getParams(user.userID, defaultAccount.accountID);

    switch (user.actionNeeded) {
        case AuthActionNeeded.ChangePassword:
          if (didSubmitLogin.current) {
            handleRedirect(`${SAMEDAY_BASE_URL}/SameDayRedesign/MyProfile/ModifyPassword.aspx?${params}`);
          }
          break;
        case AuthActionNeeded.Register:
          if (didSubmitLogin.current) {
            handleRedirect(`${SAMEDAY_BASE_URL}/SameDayRedesign/User/Registration.aspx?${params}`);
          }
          break;
        default:
          navigate(routes.DASHBOARD);
      }
  }, [cookies, user, defaultAccount]);

  const onLoginSuccess = (_user: User) => {
     didSubmitLogin.current = true;
  };
  
  const handleRedirect = (url: string) => {
    window.location.href = `${url}`;
  };

  return (
      <div className="container pt-xxxl text-center">
        <h1 className={styles.title}>Welcome! Sign in to get started...</h1>
        <LoginForm onLoginSuccess={onLoginSuccess} />
        <BottomOptions />
        <TrustPilotReviews />
    </div>
  );
};

const validationSchema: yup.Schema<LoginPayload> = yup.object({
  email: emailSchema,
  password: requiredString,
});

interface LoginFormProps {
  onLoginSuccess: (user: User) => void;
}

const LoginForm = ({ onLoginSuccess }: LoginFormProps) => {
  const [loginError, setLoginError] = useState<LoginErrorCode | ErrorCode>();

  const { logIn: loginRequest } = useAuth();

  const [logIn, isLoading] = useLoading(loginRequest);

  const trackServerError = (
    errorCode: LoginErrorCode | ErrorCode,
    httpStatus?: number
  ) => {
    track.formError({
      user_logged_in: false,
      form_id: "sign_in_form",
      form_name: "Sign In",
      error_field: "form",
      error_type: "server",
      error_message: ERROR_MAPPER[errorCode],
      error_code: httpStatus ? `${errorCode}_${httpStatus}` : `${errorCode}`,
    });
  };

  const handleFormSubmit = (data: LoginPayload) => {
    logIn(data, {
      onSuccess: (res) => {
        setLoginError(undefined);
        onLoginSuccess(res.user!);
      },
      onError: ({ response }) => {
        if (!response) {
          setLoginError(ErrorCode.NetworkError);
          trackServerError(ErrorCode.NetworkError);
          return;
        }

        if (isServerError(response.status)) {
          setLoginError(ErrorCode.ServerError);
          trackServerError(ErrorCode.ServerError, response.status);
          return;
        }

        setLoginError(response.data.errorCode);
        trackServerError(response.data.errorCode, response.status);
      },
    });
  };

  const formik = useFormik<LoginPayload>({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: handleFormSubmit,
  });

  const { values, errors, touched, handleChange } = formik;

  const handleSubmit = async () => {
    const validationErrors = await formik.validateForm();
    Object.entries(validationErrors).forEach(([field, message]) => {
      if (!message) return;
      track.formError({
        user_logged_in: false,
        form_id: "sign_in_form",
        form_name: "Sign In",
        error_field: field,
        error_type: "validation",
        error_message: message as string,
      });
    });
    formik.handleSubmit();
  };

  return (
    <div className={classNames("row", styles.login_form_container)}>
      <div className="col-md-8 mx-auto">
        <form className="row">
          <div className="col-md-6">
            <label className={styles.label} htmlFor="email-input">
              Email address
            </label>
            <div className="flex">
              <input
                id="email-input"
                className={classNames(
                  styles.input,
                  errors.email && touched.email && styles.error
                )}
                type="email"
                placeholder="Enter your email address"
                name="email"
                value={values.email}
                onChange={handleChange("email")}
                disabled={isLoading}
              />
            </div>
            <div
              className={classNames(
                styles.error,
                !(errors.email && touched.email) && "hidden"
              )}
            >
              Please enter a valid email address
            </div>
          </div>
          <div className="col-md-6">
            <label className={styles.label} htmlFor="password-input">
              Password
            </label>
            <div className="flex">
              <input
                id="password-input"
                className={classNames(
                  styles.input,
                  errors.password && touched.password && styles.error
                )}
                type="password"
                placeholder="Enter your password"
                name="password"
                value={values.password}
                onChange={handleChange("password")}
                disabled={isLoading}
              />
              <LoginButton
                disabled={isLoading}
                onClick={(e) => {
                  e.preventDefault();
                  void handleSubmit();
                }}
              />
            </div>
            <div
              className={classNames(
                styles.error,
                !(errors.password && touched.password) && "hidden"
              )}
            >
              Please enter a valid password
            </div>
            <div className="text-left">
              <a href={FORGOT_PASSWORD_URL} className={styles.change_password}>
                Change / Forgotten your password
              </a>
            </div>
          </div>
        </form>
        <div
          className={classNames(
            "bg-light-red dark-red p-xs mt-xxl",
            !loginError && "none"
          )}
        >
          {loginError && ERROR_MAPPER[loginError]}
        </div>
      </div>
    </div>
  );
};

const LoginButton = (props: ButtonProps) => (
  <button type="submit" className={styles.login_btn} {...props}>
    <ChevronRightIcon className={styles.chevron} />
  </button>
);

const BottomOptions = () => (
  <div className={styles.bottom_options}>
    <div className="row">
      <div className="col-md-6 mb-xxl">
        <h3 className={styles.subtitle}>Don't have an account?</h3>
        <hr />
        <div className="gray px-md">
          It's easy to sign up with CitySprint. Simply register for an account
          below and our team will get you setup!
        </div>
        <div className="flex justify-center pt-xxl">
          <PlainButton
            size="large"
            variant="dark"
            isLink
            href={links.SIGN_UP_FOR_AN_ACCOUNT}
          >
            REGISTER FOR AN ACCOUNT
          </PlainButton>
        </div>
      </div>
      <div className="col-md-6 mb-xxl">
        <h3 className={styles.subtitle}>
          Need online access to an existing account?
        </h3>
        <hr />
        <div className="gray px-md">
          If you're already a customer and need to activate your online account,
          you can request online access below.
        </div>
        <div className="flex justify-center pt-xxl">
          <PlainButton
            size="large"
            variant="dark"
            isLink
            href={links.REQUEST_AN_ONLINE_PROFILE}
          >
            REQUEST ONLINE ACCESS
          </PlainButton>
        </div>
      </div>
    </div>
  </div>
);

const TrustPilotReviews = () => {
  const ref = useRef(null);

  useEffect(() => {
    // If window.Trustpilot is available it means that we need to load the TrustBox from our ref.
    // If it's not, it means the script you pasted into <head /> isn't loaded  just yet.
    // When it is, it will automatically load the TrustBox.
    if ((window as any).Trustpilot) {
      (window as any).Trustpilot.loadFromElement(ref.current, true);
    }
  }, []);

  return (
    <div className="py-xxxl">
      <div
        ref={ref}
        className="trustpilot-widget"
        data-locale="en-GB"
        data-template-id={process.env.REACT_APP_TRUSTPILOT_TEMPLATE_ID}
        data-businessunit-id={process.env.REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID}
        data-style-height="140px"
        data-style-width="100%"
        data-theme="light"
        data-stars="4,5"
        data-review-languages="en"
      >
        <a
          href="https://www.trustpilot.com/review/example.com"
          target="_blank"
          rel="noopener"
        >
          Trustpilot
        </a>
      </div>
    </div>
  );
};
